import { useState, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import "@/App.css";
import { motion, useScroll, useTransform, useInView, AnimatePresence } from "framer-motion";
import { 
  ArrowRight, 
  Phone, 
  Mail, 
  MapPin, 
  ChevronDown,
  Plus,
  Send,
  Check,
  Shield,
  Zap,
  Award,
  Clock,
  ArrowUpRight
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./components/ui/sheet";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./components/ui/select";
import { Toaster, toast } from "sonner";

// Company Logo - Dark background version for dark theme
const COMPANY_LOGO = "https://customer-assets.emergentagent.com/job_vertical-solutions-1/artifacts/436z5flj_logobeyaz-png.png";

// Company Info
const COMPANY = {
  name: "FS Karadeniz Çelik Asansör",
  fullName: "FS KARADENİZ ÇELİK ASANSÖR İNŞ.OTO.TUR SAN.VE TİC.LTD.ŞTİ",
  phones: ["0539 795 81 50"],
  email: "karadenizcelik@hotmail.com",
  address: "Halil Rıfat Paşa, Yüzer Havuz Sk. Perpa Ticaret Merkezi A Blok No:1/1873 34384 Şişli/İstanbul",
  whatsapp: "905397958150"
};

// Services Data
const SERVICES = [
  {
    id: "elektrikli",
    title: "Elektrikli Asansörler",
    subtitle: "Makine Daireli & Dairesiz",
    description: "En yüksek verimlilik ve sessiz çalışma için tasarlanmış premium elektrikli asansör sistemleri.",
    specs: { durak: "2-30", kapasite: "320-5000 Kg", hiz: "2.5 m/sn" },
    image: "https://images.unsplash.com/photo-1768346564825-6f90c0b89e2e?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: "hidrolik",
    title: "Hidrolik Asansörler",
    subtitle: "Yüksek Taşıma Kapasitesi",
    description: "Ağır yükler için ideal, güvenilir ve dayanıklı hidrolik sistemler.",
    specs: { durak: "2-9", kapasite: "180-10.000 Kg", hiz: "0.74 m/sn" },
    image: "https://images.unsplash.com/photo-1758193017781-e3aee6c3e359?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: "yuk",
    title: "Yük Platformları",
    subtitle: "Endüstriyel Çözümler",
    description: "Fabrika ve depolarda verimli yük taşıma için özel tasarım platformlar.",
    specs: { durak: "2-10", kapasite: "1000-10.000 Kg", hiz: "0.15 m/sn" },
    image: "https://images.unsplash.com/photo-1770910278064-1e75bd022082?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: "engelli",
    title: "Engelli Platformu",
    subtitle: "Erişilebilir Yaşam",
    description: "Herkes için eşit erişim sağlayan, güvenli ve konforlu platformlar.",
    specs: { durak: "2-5", kapasite: "180-800 Kg", hiz: "0.15 m/sn" },
    image: "https://images.unsplash.com/photo-1754661656609-e53a3aa0417c?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: "homelift",
    title: "Homelift",
    subtitle: "Ev Tipi Asansörler",
    description: "Evinize lüks ve konfor katan, kompakt tasarımlı ev asansörleri.",
    specs: { durak: "2-5", kapasite: "160-1000 Kg", hiz: "0.15 m/sn" },
    image: "https://images.unsplash.com/photo-1622015663381-d2e05ae91b72?q=80&w=800&auto=format&fit=crop"
  },
  {
    id: "arac",
    title: "Araç Platformları",
    subtitle: "Otopark Çözümleri",
    description: "Araç parkları için alan tasarrufu sağlayan yükseltme platformları.",
    specs: { durak: "2-3", kapasite: "3000-5000 Kg", hiz: "0.8 m/sn" },
    image: "https://customer-assets.emergentagent.com/job_vertical-solutions-1/artifacts/1zep1s1q_image.png",
    imageStyle: "contain"
  }
];

// Stats Data
const STATS = [
  { number: "15", suffix: "+", label: "Yıllık Tecrübe" },
  { number: "200", suffix: "+", label: "Tamamlanan Proje" },
  { number: "200", suffix: "+", label: "Mutlu Müşteri" },
  { number: "7/24", suffix: "", label: "Teknik Destek" }
];

// References Data
const REFERENCES = [
  {
    image: "https://customer-assets.emergentagent.com/job_vertical-solutions-1/artifacts/clcsferk_image.png",
    title: "Lüks Rezidans Projesi",
    location: "İstanbul"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_vertical-solutions-1/artifacts/b5atz4ka_image.png",
    title: "Premium Konut Kompleksi",
    location: "İstanbul"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_vertical-solutions-1/artifacts/roqfnvt9_image.png",
    title: "Forev Modern Haliç",
    location: "İstanbul"
  },
  {
    image: "https://customer-assets.emergentagent.com/job_vertical-solutions-1/artifacts/dfgu1s3q_image.png",
    title: "Forev Modern Vadi",
    location: "İstanbul"
  }
];

// Tech Specs
const TECH_SPECS = [
  {
    title: "Boyutlar ve Kapasite",
    items: ["Kabin: Özel ölçülere göre üretim", "Kapasite: 180 Kg - 10.000 Kg", "Durak: 2 - 30 arası"]
  },
  {
    title: "Güvenlik Sistemleri",
    items: ["Acil fren sistemi", "Hız regülatörü", "Tampon sistemi", "Kapı kilitleri"]
  },
  {
    title: "Kontrol Özellikleri",
    items: ["Akıllı kumanda sistemi", "Enerji tasarruflu LED aydınlatma", "Uzaktan izleme seçeneği"]
  }
];

// Hero Background Image
const HERO_BG = "https://images.unsplash.com/photo-1771760563774-cef015508efe?q=80&w=1920&auto=format&fit=crop";

// Fade In Animation Variant
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.23, 1, 0.32, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

// Navigation Component
function Navigation({ onQuoteClick }) {
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    if (!isHomePage) {
      navigate("/");
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
      }, 100);
    } else {
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  const goToReferences = () => {
    navigate("/referanslar");
    window.scrollTo(0, 0);
  };

  const goHome = () => {
    navigate("/");
    window.scrollTo(0, 0);
  };

  return (
    <motion.nav
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.23, 1, 0.32, 1] }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled ? "nav-blur py-4" : "py-6 bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <motion.div 
          className="flex items-center gap-3 cursor-pointer"
          whileHover={{ scale: 1.02 }}
          onClick={goHome}
        >
          <img 
            src={COMPANY_LOGO} 
            alt="FS Karadeniz Çelik Logo" 
            className="h-20 w-auto object-contain"
          />
        </motion.div>

        <div className="hidden md:flex items-center gap-10">
          {[
            { id: "hakkimizda", label: "Hakkımızda" },
            { id: "hizmetler", label: "Hizmetler" },
            { id: "teknik", label: "Teknik" },
            { id: "iletisim", label: "İletişim" }
          ].map((item) => (
            <button
              key={item.id}
              onClick={() => scrollTo(item.id)}
              className="text-sm text-white/60 hover:text-white transition-colors duration-300"
              data-testid={`nav-${item.id}`}
            >
              {item.label}
            </button>
          ))}
          <button
            onClick={goToReferences}
            className="text-sm text-white/60 hover:text-white transition-colors duration-300"
            data-testid="nav-referanslar"
          >
            Referanslar
          </button>
        </div>

        <Button
          onClick={onQuoteClick}
          className="bg-white text-black hover:bg-white/90 rounded-full px-6 h-11 text-sm font-medium shadow-lg shadow-white/10"
          data-testid="nav-quote-btn"
        >
          Teklif Al
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </motion.nav>
  );
}

// Hero Section
function HeroSection({ onQuoteClick }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"]
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 100]);
  const bgY = useTransform(scrollYProgress, [0, 1], [0, 200]);

  return (
    <section ref={ref} className="hero-section" data-testid="hero-section">
      {/* Background Image */}
      <motion.div 
        style={{ y: bgY }}
        className="absolute inset-0 z-0"
      >
        <div 
          className="absolute inset-0 bg-cover bg-center bg-no-repeat"
          style={{ backgroundImage: `url(${HERO_BG})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/70 via-[#0a0a0a]/50 to-[#0a0a0a]" />
      </motion.div>
      
      {/* Floating Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-10">
        <motion.div 
          className="absolute top-1/4 left-10 w-2 h-2 rounded-full bg-blue-500/30"
          animate={{ y: [-20, 20, -20], opacity: [0.3, 0.6, 0.3] }}
          transition={{ duration: 5, repeat: Infinity }}
        />
        <motion.div 
          className="absolute top-1/3 right-20 w-3 h-3 rounded-full bg-purple-500/20"
          animate={{ y: [20, -20, 20], opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 7, repeat: Infinity }}
        />
        <motion.div 
          className="absolute bottom-1/3 left-1/4 w-1 h-1 rounded-full bg-white/20"
          animate={{ y: [-15, 15, -15] }}
          transition={{ duration: 4, repeat: Infinity }}
        />
      </div>

      <motion.div 
        style={{ opacity, scale, y }}
        className="relative z-10 max-w-7xl mx-auto px-6 pt-32 pb-20"
      >
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.8 }}
          className="flex items-center gap-3 mb-8"
        >
          <div className="h-px w-12 bg-gradient-to-r from-blue-500 to-transparent" />
          <span className="text-xs uppercase tracking-[0.3em] text-white/50">
            Premium Asansör Sistemleri
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 1, ease: [0.23, 1, 0.32, 1] }}
          className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-bold tracking-tight leading-[0.9] mb-8"
        >
          <span className="text-white">Yükselişin</span>
          <br />
          <span className="gradient-text-animated">Yeni Standardı.</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.6, duration: 0.8 }}
          className="text-lg md:text-xl text-white/50 max-w-xl mb-12 leading-relaxed"
        >
          15 yılı aşkın tecrübemizle, en yüksek güvenlik standartlarında 
          modern asansör çözümleri sunuyoruz.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.8, duration: 0.8 }}
          className="flex flex-col sm:flex-row gap-4"
        >
          <Button
            onClick={onQuoteClick}
            size="lg"
            className="bg-white text-black hover:bg-white/90 rounded-full px-8 h-14 text-base font-medium group"
            data-testid="hero-quote-btn"
          >
            Ücretsiz Teklif Alın
            <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
          </Button>
          <Button
            onClick={() => document.getElementById("hizmetler")?.scrollIntoView({ behavior: "smooth" })}
            size="lg"
            variant="outline"
            className="border-white/20 text-white hover:bg-white/5 rounded-full px-8 h-14 text-base"
            data-testid="hero-services-btn"
          >
            Keşfet
            <ChevronDown className="ml-2 h-5 w-5" />
          </Button>
        </motion.div>
      </motion.div>

      {/* Marquee */}
      <div className="marquee-container relative z-10">
        <div className="marquee-text">
          {[...Array(4)].map((_, i) => (
            <span key={i}>FS Karadeniz Çelik • Asansör • Güvenlik • Kalite • </span>
          ))}
        </div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] uppercase tracking-widest text-white/30">Scroll</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
          className="w-5 h-8 rounded-full border border-white/20 flex justify-center pt-2"
        >
          <div className="w-1 h-2 rounded-full bg-white/40" />
        </motion.div>
      </motion.div>
    </section>
  );
}

// Animated Counter Component
function AnimatedCounter({ value, suffix = "", duration = 2 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const isSpecial = value === "7/24";

  useEffect(() => {
    if (!isInView || isSpecial) return;

    const target = parseInt(value);
    const increment = target / (duration * 60); // 60fps
    let current = 0;

    const timer = setInterval(() => {
      current += increment;
      if (current >= target) {
        setCount(target);
        clearInterval(timer);
      } else {
        setCount(Math.floor(current));
      }
    }, 1000 / 60);

    return () => clearInterval(timer);
  }, [isInView, value, duration, isSpecial]);

  return (
    <span ref={ref}>
      {isSpecial ? value : count}<span className="text-blue-500">{suffix}</span>
    </span>
  );
}

// Stats Section
function StatsSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} id="hakkimizda" className="section-padding relative" data-testid="stats-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-2 md:grid-cols-4 gap-1 bg-white/5 rounded-3xl overflow-hidden"
        >
          {STATS.map((stat, index) => (
            <motion.div
              key={stat.label}
              variants={fadeInUp}
              className="stat-display bg-[#0a0a0a]"
              data-testid={`stat-${index}`}
            >
              <div className="stat-number">
                <AnimatedCounter 
                  value={stat.number} 
                  suffix={stat.suffix} 
                  duration={2.5} 
                />
              </div>
              <div className="stat-label">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// Feature Section with Image
function FeatureSection() {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="section-padding gradient-mesh">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-blue-500 mb-4 block">Neden Biz?</span>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-6 leading-tight">
              Güvenlik, Mühendislik ve Kalitenin
              <span className="text-white/40"> Kusursuz Uyumu.</span>
            </h2>
            <p className="text-white/50 text-lg leading-relaxed mb-6">
              Asansör sistemlerinde güvenlik ve kaliteyi standart değil, temel prensip olarak görüyoruz. 
              Her projede ulusal ve uluslararası normlara uygun, yüksek performanslı ve uzun ömürlü çözümler sunuyoruz.
            </p>
            <p className="text-white/50 text-lg leading-relaxed mb-8">
              Mühendislik gücümüz, teknik uzmanlığımız ve titiz uygulama süreçlerimizle; konut, ticari ve 
              endüstriyel projelerde sürdürülebilir ve güvenilir asansör sistemleri geliştiriyoruz.
            </p>
            
            <div className="space-y-6">
              {[
                { icon: Shield, title: "Ulusal & Uluslararası Standartlar", desc: "Normlara uygun sistemler" },
                { icon: Zap, title: "Yüksek Performans", desc: "Verimli ve sessiz çalışma" },
                { icon: Award, title: "Uzun Ömürlü Çözümler", desc: "Dayanıklı ve güvenilir sistemler" }
              ].map((item, i) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.2 + i * 0.1 }}
                  className="flex items-start gap-4"
                >
                  <div className="w-12 h-12 rounded-2xl bg-blue-500/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="font-semibold mb-1">{item.title}</h4>
                    <p className="text-white/40 text-sm">{item.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            <div className="feature-image-container aspect-[4/5]">
              <img 
                src="https://images.unsplash.com/photo-1759222196651-cac4bb9da047?q=80&w=800&auto=format&fit=crop"
                alt="Modern Asansör"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -left-8 glass rounded-2xl p-6 max-w-xs">
              <div className="flex items-center gap-3 mb-3">
                <Clock className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
                <span className="font-semibold">7/24 Teknik Destek</span>
              </div>
              <p className="text-sm text-white/50">Acil durumlarda anında müdahale</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Services Section
function ServicesSection({ onQuoteClick }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} id="hizmetler" className="section-padding" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-blue-500 mb-4 block">Hizmetlerimiz</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
            Her ihtiyaca
            <span className="text-white/40"> özel çözüm.</span>
          </h2>
        </motion.div>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
        >
          {SERVICES.map((service, index) => (
            <motion.div
              key={service.id}
              variants={fadeInUp}
              className="service-detail-card group"
              data-testid={`service-${service.id}`}
            >
              <div className={`aspect-video rounded-xl overflow-hidden mb-6 ${service.imageStyle === "contain" ? "bg-[#c8c8c8]" : ""}`}>
                <img 
                  src={service.image} 
                  alt={service.title}
                  className={`w-full h-full transition-transform duration-700 group-hover:scale-110 ${
                    service.imageStyle === "contain" ? "object-contain" : "object-cover"
                  }`}
                />
              </div>
              <span className="text-xs uppercase tracking-wider text-blue-500">{service.subtitle}</span>
              <h3 className="text-xl font-semibold mt-2 mb-3">{service.title}</h3>
              <p className="text-white/40 text-sm mb-6 leading-relaxed">{service.description}</p>
              
              <div className="flex flex-wrap gap-2 mb-6">
                {Object.entries(service.specs).map(([key, value]) => (
                  <span key={key} className="text-xs px-3 py-1.5 rounded-full bg-white/5 text-white/60">
                    {value}
                  </span>
                ))}
              </div>

              <button
                onClick={onQuoteClick}
                className="flex items-center gap-2 text-sm text-white/60 hover:text-blue-500 transition-colors group/btn"
                data-testid={`service-quote-${service.id}`}
              >
                Teklif Al
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
              </button>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// Technical Specs Section
function TechSpecsSection() {
  const [openIndex, setOpenIndex] = useState(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} id="teknik" className="section-padding gradient-mesh" data-testid="tech-section">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
          >
            <span className="text-xs uppercase tracking-[0.2em] text-blue-500 mb-4 block">Teknik Detaylar</span>
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              Mühendislik
              <span className="text-white/40"> mükemmelliği.</span>
            </h2>
            <p className="text-white/50 text-lg leading-relaxed mb-8">
              Tüm asansör sistemlerimiz, uzman mühendislerimiz tarafından 
              en son teknoloji ile tasarlanmaktadır.
            </p>

            <div className="space-y-0">
              {TECH_SPECS.map((spec, index) => (
                <div 
                  key={spec.title} 
                  className="accordion-item"
                  data-open={openIndex === index}
                >
                  <button
                    className="accordion-trigger"
                    onClick={() => setOpenIndex(openIndex === index ? null : index)}
                    data-testid={`accordion-${index}`}
                  >
                    <span>{spec.title}</span>
                    <div className="accordion-icon">
                      <Plus className="h-4 w-4" strokeWidth={1.5} />
                    </div>
                  </button>
                  <AnimatePresence>
                    {openIndex === index && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        className="overflow-hidden"
                      >
                        <ul className="pb-6 space-y-3">
                          {spec.items.map((item, i) => (
                            <li key={i} className="flex items-center gap-3 text-white/50">
                              <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.2 }}
            className="relative"
          >
            <div className="aspect-square rounded-3xl overflow-hidden">
              <img 
                src="https://images.unsplash.com/photo-1768346564825-6f90c0b89e2e?q=80&w=800&auto=format&fit=crop"
                alt="Asansör Teknolojisi"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0a] via-transparent to-transparent" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// References Page (Separate Page)
function ReferencesPage({ onQuoteClick }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <div className="min-h-screen pt-32">
      <section ref={ref} className="section-padding" data-testid="references-page">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            className="text-center mb-16"
          >
            <span className="text-xs uppercase tracking-[0.2em] text-blue-500 mb-4 block">Referanslar</span>
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
              Projelerimizden
              <span className="text-white/40"> bir kaçı.</span>
            </h2>
            <p className="text-white/50 text-lg max-w-2xl mx-auto">
              Türkiye'nin önde gelen inşaat firmalarıyla gerçekleştirdiğimiz prestijli projelerden sadece bir kaçı.
            </p>
          </motion.div>

          <motion.div
            variants={staggerContainer}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="grid md:grid-cols-2 gap-6"
          >
            {REFERENCES.map((project, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="group relative overflow-hidden rounded-2xl aspect-[16/10]"
                data-testid={`reference-${index}`}
              >
                <img 
                  src={project.image} 
                  alt={project.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-6">
                  <h3 className="text-xl font-semibold text-white mb-1">{project.title}</h3>
                  <p className="text-white/60 text-sm">{project.location}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Büyük Kulüp Logo */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.4 }}
            className="mt-16 text-center"
          >
            <p className="text-white/40 text-sm uppercase tracking-wider mb-6">Güvenilir İş Ortaklarımız</p>
            <div className="flex justify-center">
              <div className="bg-white rounded-2xl p-6 w-40 h-40 flex items-center justify-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_vertical-solutions-1/artifacts/h7ov6oor_image.png"
                  alt="Büyük Kulüp"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.5 }}
            className="mt-20 text-center"
          >
            <Button
              onClick={onQuoteClick}
              size="lg"
              className="bg-white text-black hover:bg-white/90 rounded-full px-10 h-14 text-base font-medium"
              data-testid="ref-quote-btn"
            >
              Projeniz İçin Teklif Alın
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        </div>
      </section>
      <Footer />
    </div>
  );
}

// Main Home Page
function CTASection({ onQuoteClick }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <section ref={ref} className="py-16 px-6">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={isInView ? { opacity: 1, y: 0 } : {}}
        className="max-w-7xl mx-auto cta-section p-12 md:p-20"
      >
        <div className="relative z-10 text-center">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tight mb-6">
            Projeniz için teklif alın.
          </h2>
          <p className="text-white/50 text-lg max-w-2xl mx-auto mb-10">
            Uzman ekibimiz sizinle iletişime geçerek, ihtiyaçlarınıza 
            en uygun çözümü sunacaktır.
          </p>
          <Button
            onClick={onQuoteClick}
            size="lg"
            className="bg-white text-black hover:bg-white/90 rounded-full px-10 h-14 text-base font-medium"
            data-testid="cta-quote-btn"
          >
            Hemen Teklif Alın
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </motion.div>
    </section>
  );
}

// Contact Section
function ContactSection() {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch("https://formsubmit.co/ajax/karadenizcelik@hotmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          message: formData.message,
          _subject: "Yeni İletişim Mesajı - FS Karadeniz Çelik"
        })
      });
      
      if (response.ok) {
        toast.success("Mesajınız başarıyla gönderildi!");
        setFormData({ name: "", email: "", message: "" });
      } else {
        throw new Error("Gönderim hatası");
      }
    } catch {
      toast.error("Bir hata oluştu. Lütfen tekrar deneyin.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section ref={ref} id="iletisim" className="section-padding" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          className="text-center mb-16"
        >
          <span className="text-xs uppercase tracking-[0.2em] text-blue-500 mb-4 block">İletişim</span>
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight">
            Bize ulaşın.
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-16">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            className="space-y-8"
          >
            <div>
              <h3 className="text-2xl font-semibold mb-2">{COMPANY.name}</h3>
              <p className="text-white/40 text-sm">{COMPANY.fullName}</p>
            </div>

            <div className="space-y-6">
              <a 
                href={`tel:${COMPANY.phones[0].replace(/\s/g, '')}`}
                className="flex items-center gap-4 group"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center group-hover:bg-blue-500/10 transition-colors">
                  <Phone className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-wider">Telefon</p>
                  <p className="text-lg font-medium">{COMPANY.phones[0]}</p>
                </div>
              </a>

              <a 
                href={`mailto:${COMPANY.email}`}
                className="flex items-center gap-4 group"
                data-testid="contact-email"
              >
                <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center group-hover:bg-blue-500/10 transition-colors">
                  <Mail className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-wider">E-posta</p>
                  <p className="text-lg font-medium">{COMPANY.email}</p>
                </div>
              </a>

              <div className="flex items-start gap-4" data-testid="contact-address">
                <div className="w-14 h-14 rounded-2xl bg-white/5 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-5 w-5 text-blue-500" strokeWidth={1.5} />
                </div>
                <div>
                  <p className="text-xs text-white/40 uppercase tracking-wider">Adres</p>
                  <p className="text-sm leading-relaxed text-white/80">{COMPANY.address}</p>
                </div>
              </div>
            </div>

            <a 
              href="https://www.google.com/maps/place/Perpa+Ticaret+Merkezi/@41.0642,28.9494,17z"
              target="_blank"
              rel="noopener noreferrer"
              className="block map-container h-64 relative group cursor-pointer"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3008.7893893893894!2d28.9494!3d41.0642!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cab7650656bd63%3A0x8ca058b28c20b6c3!2sPerpa%20Ticaret%20Merkezi!5e0!3m2!1str!2str!4v1700000000000!5m2!1str!2str"
                width="100%"
                height="100%"
                style={{ border: 0, pointerEvents: "none" }}
                allowFullScreen=""
                loading="lazy"
                title="Konum"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                <span className="opacity-0 group-hover:opacity-100 transition-opacity bg-white text-black px-4 py-2 rounded-full text-sm font-medium">
                  Haritada Aç
                </span>
              </div>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
          >
            <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
              <Input
                placeholder="Adınız"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="form-input h-14"
                data-testid="contact-name-input"
              />
              <Input
                type="email"
                placeholder="E-posta Adresiniz"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="form-input h-14"
                data-testid="contact-email-input"
              />
              <Textarea
                placeholder="Mesajınız"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                rows={6}
                className="form-input resize-none"
                data-testid="contact-message-input"
              />
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-white text-black hover:bg-white/90 rounded-full h-14 text-base font-medium"
                data-testid="contact-submit-btn"
              >
                {loading ? <div className="spinner" /> : (
                  <>Gönder <Send className="ml-2 h-4 w-4" /></>
                )}
              </Button>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

// Quote Form
function QuoteForm({ open, onOpenChange }) {
  const [formData, setFormData] = useState({
    name: "", surname: "", email: "", phone: "", company: "",
    service_type: "", floor_count: "", capacity: "", message: ""
  });
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const serviceNames = {
    "elektrikli": "Elektrikli Asansörler",
    "hidrolik": "Hidrolik Asansörler",
    "yuk": "Yük Platformları",
    "engelli": "Engelli Platformu",
    "homelift": "Homelift",
    "arac": "Araç Platformları",
    "projelendirme": "Projelendirme Hizmeti",
    "statik-rapor": "Statik Rapor Hizmeti",
    "kesif": "Keşif Hizmeti",
    "danismanlik": "Danışmanlık"
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch("https://formsubmit.co/ajax/karadenizcelik@hotmail.com", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Accept": "application/json"
        },
        body: JSON.stringify({
          _subject: "Yeni Teklif Talebi - FS Karadeniz Çelik",
          "Ad": formData.name,
          "Soyad": formData.surname,
          "E-posta": formData.email,
          "Telefon": formData.phone,
          "Firma": formData.company || "-",
          "Hizmet Türü": serviceNames[formData.service_type] || formData.service_type,
          "Durak Sayısı": formData.floor_count || "-",
          "Kapasite": formData.capacity || "-",
          "Mesaj": formData.message || "-"
        })
      });
      
      if (response.ok) {
        setSuccess(true);
        toast.success("Teklif talebiniz alındı!");
        setTimeout(() => {
          onOpenChange(false);
          setSuccess(false);
          setFormData({
            name: "", surname: "", email: "", phone: "", company: "",
            service_type: "", floor_count: "", capacity: "", message: ""
          });
        }, 2000);
      } else {
        throw new Error("Gönderim hatası");
      }
    } catch {
      toast.error("Bir hata oluştu.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-lg glass-strong border-white/10 overflow-y-auto" data-testid="quote-sheet">
        <SheetHeader className="mb-8">
          <SheetTitle className="text-2xl font-bold text-white">Teklif Talebi</SheetTitle>
          <p className="text-white/40 text-sm">Formu doldurun, size en kısa sürede dönüş yapalım.</p>
        </SheetHeader>

        <AnimatePresence mode="wait">
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-col items-center justify-center py-20"
            >
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mb-6">
                <Check className="h-10 w-10 text-green-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Teşekkürler!</h3>
              <p className="text-white/40">Talebiniz alındı.</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4" data-testid="quote-form">
              <div className="grid grid-cols-2 gap-4">
                <Input placeholder="Adınız *" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} required className="form-input" data-testid="quote-name-input" />
                <Input placeholder="Soyadınız *" value={formData.surname} onChange={(e) => setFormData({...formData, surname: e.target.value})} required className="form-input" data-testid="quote-surname-input" />
              </div>
              <Input type="email" placeholder="E-posta *" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} required className="form-input" data-testid="quote-email-input" />
              <Input placeholder="Telefon *" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} required className="form-input" data-testid="quote-phone-input" />
              <Input placeholder="Firma Adı" value={formData.company} onChange={(e) => setFormData({...formData, company: e.target.value})} className="form-input" data-testid="quote-company-input" />
              
              <Select value={formData.service_type} onValueChange={(v) => setFormData({...formData, service_type: v})} required>
                <SelectTrigger className="form-input h-12" data-testid="quote-service-select">
                  <SelectValue placeholder="Hizmet Türü *" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10">
                  {SERVICES.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="text-white hover:bg-white/10">{s.title}</SelectItem>
                  ))}
                  <SelectItem value="projelendirme" className="text-white hover:bg-white/10">Projelendirme Hizmeti</SelectItem>
                  <SelectItem value="statik-rapor" className="text-white hover:bg-white/10">Statik Rapor Hizmeti</SelectItem>
                  <SelectItem value="kesif" className="text-white hover:bg-white/10">Keşif Hizmeti</SelectItem>
                  <SelectItem value="danismanlik" className="text-white hover:bg-white/10">Danışmanlık</SelectItem>
                </SelectContent>
              </Select>

              <div className="grid grid-cols-2 gap-4">
                <Input type="number" placeholder="Durak Sayısı" value={formData.floor_count} onChange={(e) => setFormData({...formData, floor_count: e.target.value})} className="form-input" data-testid="quote-floor-input" />
                <Select value={formData.capacity} onValueChange={(v) => setFormData({...formData, capacity: v})}>
                  <SelectTrigger className="form-input h-12" data-testid="quote-capacity-select">
                    <SelectValue placeholder="Kapasite" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10">
                    <SelectItem value="4-kisi" className="text-white hover:bg-white/10">4 Kişilik (320 kg)</SelectItem>
                    <SelectItem value="6-kisi" className="text-white hover:bg-white/10">6 Kişilik (450 kg)</SelectItem>
                    <SelectItem value="8-kisi" className="text-white hover:bg-white/10">8 Kişilik (630 kg)</SelectItem>
                    <SelectItem value="10-kisi" className="text-white hover:bg-white/10">10 Kişilik (800 kg)</SelectItem>
                    <SelectItem value="diger" className="text-white hover:bg-white/10">Diğer</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Textarea placeholder="Proje Detayları" value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={4} className="form-input resize-none" data-testid="quote-message-input" />

              <Button type="submit" disabled={loading} className="w-full bg-white text-black hover:bg-white/90 rounded-full h-14 text-base font-medium" data-testid="quote-submit-btn">
                {loading ? <div className="spinner" /> : <>Gönder <Send className="ml-2 h-4 w-4" /></>}
              </Button>
            </form>
          )}
        </AnimatePresence>
      </SheetContent>
    </Sheet>
  );
}

// WhatsApp Button
function WhatsAppButton() {
  return (
    <motion.a
      href={`https://wa.me/${COMPANY.whatsapp}`}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-btn animate-pulse-glow"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 2, type: "spring" }}
      whileHover={{ scale: 1.1 }}
      data-testid="whatsapp-btn"
    >
      <svg viewBox="0 0 24 24" className="h-7 w-7 fill-white">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
    </motion.a>
  );
}

// Footer
function Footer() {
  return (
    <footer className="py-12" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-3">
            <img 
              src={COMPANY_LOGO} 
              alt="FS Karadeniz Çelik Logo" 
              className="h-20 w-auto object-contain"
            />
          </div>
          <p className="text-white/30 text-sm">
            © {new Date().getFullYear()} Tüm hakları saklıdır.
          </p>
        </div>
      </div>
    </footer>
  );
}

// Main Home Page
function HomePage({ onQuoteClick }) {
  return (
    <>
      <HeroSection onQuoteClick={onQuoteClick} />
      <StatsSection />
      <FeatureSection />
      <ServicesSection onQuoteClick={onQuoteClick} />
      <TechSpecsSection />
      <CTASection onQuoteClick={onQuoteClick} />
      <ContactSection />
      <Footer />
    </>
  );
}

// Main App
function App() {
  const [quoteOpen, setQuoteOpen] = useState(false);

  return (
    <BrowserRouter>
      <div className="min-h-screen">
        <Toaster position="top-center" theme="dark" />
        <Navigation onQuoteClick={() => setQuoteOpen(true)} />
        <Routes>
          <Route path="/" element={<HomePage onQuoteClick={() => setQuoteOpen(true)} />} />
          <Route path="/referanslar" element={<ReferencesPage onQuoteClick={() => setQuoteOpen(true)} />} />
        </Routes>
        <WhatsAppButton />
        <QuoteForm open={quoteOpen} onOpenChange={setQuoteOpen} />
      </div>
    </BrowserRouter>
  );
}

export default App;
